import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user, _hasHydrated, setHasHydrated } = useAuthStore();
  const location = useLocation();
  const [forceHydrated, setForceHydrated] = useState(false);

  // Fallback: if hydration doesn't complete within 2 seconds, force it
  useEffect(() => {
    if (!_hasHydrated && !forceHydrated) {
      const timer = setTimeout(() => {
        setHasHydrated(true);
        setForceHydrated(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [_hasHydrated, forceHydrated, setHasHydrated]);

  // Wait for zustand to finish rehydrating from localStorage
  // Show nothing while hydrating to prevent flash of login redirect
  if (!_hasHydrated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && user && !requiredRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
